import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { AlertTriangle, CheckCircle } from 'lucide-react';

const License = () => {
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysRemaining, setDaysRemaining] = useState(0);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    fetchLicense();
  }, []);

  const fetchLicense = async () => {
    try {
      const response = await api.get('/admin/license');
      const licenseData = response.data;
      setLicense(licenseData);

      // Calculate days remaining
      const validUntil = new Date(licenseData.valid_until);
      const now = new Date();
      const diffTime = validUntil - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setDaysRemaining(diffDays);
      setIsExpired(diffDays <= 0);
    } catch (error) {
      console.error('Failed to fetch license:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div data-testid="loading-indicator">Loading license information...</div>;
  }

  return (
    <div data-testid="license-page">
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '24px' }}>License Information</h1>

      <div className="card" style={{ maxWidth: '600px' }}>
        <div className="flex items-center gap-4 mb-4">
          {isExpired ? (
            <AlertTriangle size={48} color="#F87171" />
          ) : daysRemaining <= 30 ? (
            <AlertTriangle size={48} color="#FBBF24" />
          ) : (
            <CheckCircle size={48} color="#34D399" />
          )}
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>
              {isExpired ? 'License Expired' : 'License Active'}
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
              {isExpired
                ? 'System in read-only mode. Only Admin can login.'
                : daysRemaining <= 30
                ? `Expires in ${daysRemaining} days`
                : `${daysRemaining} days remaining`}
            </p>
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: '16px' }}>
          <div className="grid grid-cols-2" style={{ gap: '16px' }}>
            <div>
              <div className="form-label">License Key</div>
              <div className="font-mono" style={{ fontSize: '14px' }} data-testid="license-key">
                {license?.key}
              </div>
            </div>
            <div>
              <div className="form-label">Valid Until</div>
              <div style={{ fontSize: '14px' }} data-testid="valid-until">
                {new Date(license?.valid_until).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </div>
            </div>
            <div>
              <div className="form-label">Status</div>
              <span
                className={`badge ${license?.is_active ? 'badge-success' : 'badge-danger'}`}
                data-testid="license-status"
              >
                {license?.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div>
              <div className="form-label">Days Remaining</div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: isExpired ? '#991B1B' : daysRemaining <= 30 ? '#92400E' : '#065F46',
                }}
                data-testid="days-remaining"
              >
                {isExpired ? '0 (Expired)' : daysRemaining}
              </div>
            </div>
          </div>
        </div>

        {(isExpired || daysRemaining <= 30) && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: isExpired ? '#FEE2E2' : '#FEF3C7',
              border: `1px solid ${isExpired ? '#F87171' : '#FBBF24'}`,
              borderRadius: '4px',
              fontSize: '13px',
              color: isExpired ? '#991B1B' : '#92400E',
            }}
            data-testid="license-warning"
          >
            {isExpired
              ? 'Your license has expired. Please contact support to renew your license.'
              : 'Your license is expiring soon. Please renew to avoid service interruption.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default License;
